import {Component, OnInit, ChangeDetectionStrategy} from "@angular/core";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {ActivatedRoute} from "@angular/router";
import {HttpErrorResponse} from "@angular/common/http";
import {lastValueFrom} from "rxjs";
import {PerFormaAPIService, ResetPasswordRequest} from "src/app/services/pfapi.service";

@Component({
    selector: "app-reset-password",
    templateUrl: "./reset-password.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ResetPasswordComponent implements OnInit {
    form: FormGroup;
    submitting = false;
    submitted = false;
    errorMessage = "";

    // base64url-encoded Identity reset token from the email link (?code=...)
    private code = "";

    constructor(
        private readonly fb: FormBuilder,
        private readonly route: ActivatedRoute,
        private readonly api: PerFormaAPIService
    ) {
        this.form = this.fb.group({
            email: ["", [Validators.required]],
            password: ["", [Validators.required, Validators.minLength(6)]],
            confirmPassword: ["", [Validators.required]]
        });
    }

    ngOnInit(): void {
        const params = this.route.snapshot.queryParamMap;
        this.code = params.get("code") || "";
        const email = params.get("email") || "";
        if (email) this.form.patchValue({email});
    }

    async onSubmit(): Promise<void> {
        this.errorMessage = "";

        if (this.form.invalid) {
            Object.keys(this.form.controls).forEach(k => this.form.get(k)?.markAsTouched());
            return;
        }

        const {email, password, confirmPassword} = this.form.value;

        if (password !== confirmPassword) {
            this.errorMessage = "The password and confirmation password do not match.";
            return;
        }
        if (!this.code) {
            this.errorMessage = "Invalid or missing reset link. Please request a new one.";
            return;
        }

        const request: ResetPasswordRequest = {Email: email, ResetCode: this.code, NewPassword: password};

        this.submitting = true;
        try {
            await lastValueFrom(this.api.postApiAuthResetPassword(request));
            this.submitted = true;
        } catch (err) {
            this.errorMessage = this.extractError(err);
        } finally {
            this.submitting = false;
        }
    }

    private extractError(err: unknown): string {
        if (err instanceof HttpErrorResponse && err.error) {
            // identity validation problems come back as { errors: { code: [message] } }
            const errors = err.error.errors;
            if (errors && typeof errors === "object") {
                const messages = (Object.values(errors) as string[][]).flat();
                if (messages.length) return messages.join(" ");
            }
            if (typeof err.error.detail === "string") return err.error.detail;
        }
        return "Unable to reset your password. The link may have expired — request a new one.";
    }
}
